import { Injectable } from 'angular-ts-decorators';
import { Domain } from './domain';

interface FashionChequeSuccessResponse {
  status: 'ok';
  data: {
    card?: {
      status: string;
      amount: {
        value: number;
        currency: string;
      };
      expireDate: string;
      refillsRemaining: number;
      transactionsRemaining: number;
      initialValue: number;
      maxValue: number;
      minValue: number;
      hasVerificationCode: boolean;
      number: string;
    };
    requestId?: string;
  };
}

interface FashionChequeErrorResponse {
  status: 'error';
  type: string;
  message: string;
  developer_message: string;
}

export type FashionChequeResponse =
  | FashionChequeSuccessResponse
  | FashionChequeErrorResponse;

/**
 * Service that provides functionality to interact with fashion cheques.
 */
@Injectable('fashionCheque')
export class FashionCheque {
  private readonly fashionChequesPrefixes = [
    '60464251',
    '62992000',
    '22075600',
    '22075700',
    '22075800',
  ];

  constructor(
    private $http: ng.IHttpService,
    private domain: Domain,
  ) {
    'ngInject';
  }

  /**
   * Checks if the given fashion cheque code is valid.
   */
  validateCode(code: string): boolean {
    return (
      code.length === 19 &&
      this.fashionChequesPrefixes.some((prefix) => code.startsWith(prefix))
    );
  }

  /**
   * Checks if the given fashion cheque voucher is valid.
   * @param number
   * Fashion cheque voucher number
   * @return
   * Resolve with http response promise on successfully returns voucher data, reject otherwise.
   */
  checkVoucher(number: string): ng.IPromise<FashionChequeResponse> {
    return this.$http
      .post<FashionChequeResponse>(
        `${this.domain.getSpynl()}/pos/check-fashioncheque-card`,
        { data: { number } },
        {
          headers: { 'Content-Type': 'text/xml; charset=utf-8' },
          withCredentials: true,
        },
      )
      .then((response) => response.data);
  }

  /**
   * Redeems a fashion cheque voucher.
   * @param number
   * Fashion cheque voucher number
   * @param amount
   * Amount to redeem
   * @return
   * Resolve
   **/
  redeemVoucher(
    number: string,
    amount: number,
  ): ng.IPromise<FashionChequeResponse> {
    return this.$http
      .post<FashionChequeResponse>(
        `${this.domain.getSpynl()}/pos/redeem-fashioncheque-card`,
        { data: { number, amount } },
        {
          headers: { 'Content-Type': 'text/xml; charset=utf-8' },
          withCredentials: true,
        },
      )
      .then((response) => response.data);
  }

  /**
   * Rollback a fashion cheque voucher.
   * @param number
   * Fashion cheque voucher number
   * @param originalRequestId
   * Original request id
   * @return
   * Resolve
   **/
  rollbackVoucher(
    number: string,
    originalRequestId: string,
  ): ng.IPromise<FashionChequeResponse> {
    return this.$http
      .post<FashionChequeResponse>(
        `${this.domain.getSpynl()}/pos/rollback-fashioncheque-card`,
        { data: { number, originalRequestId } },
        {
          headers: { 'Content-Type': 'text/xml; charset=utf-8' },
          withCredentials: true,
        },
      )
      .then((response) => response.data);
  }
}
